/** @jsx jsx */
import type { ReactNode } from 'react';
import { Inline } from '@balance-web/inline';
import { jsx } from '@balance-web/core';
import { Text } from '@balance-web/text';
import { theme, themeRaw } from '@balance-web/theme';
import { ExternalLinkIcon } from '@balance-web/icon';
import { CheckIcon } from '@balance-web/icon';
import { Link } from '@balance-web/link';

import { useRadioGroupContext } from './context';
import type { AnchorProps } from './types';
import { RadioItemIndicator } from './radix-styled-components';

export type ItemSelectionProps =
  | {
      selected: boolean;
      selectedLabel: string;
    }
  | {
      selected?: never;
      selectedLabel?: never;
    };

export type ItemBaseProps = AnchorProps & {
  tone?: 'passive' | 'critical';
  startElement?: ReactNode;
  endElement?: ReactNode;
  disabled?: boolean;
  children?: ReactNode;
} & ItemSelectionProps;

export const ItemBase = ({
  children,
  startElement,
  endElement,
  disabled,
  selected,
  selectedLabel,
  tone,
  ...props
}: ItemBaseProps) => {
  const isInRadioGroup = useRadioGroupContext();
  const styles = useStyles({
    disabled,
    tone,
  });

  const disabledOrBaseColor = disabled ? 'textDisabled' : 'textPrimary';
  const iconSize = 'small';

  /** Padding to make space for absolutely position radio item indicator */
  const radioItemLeftPadding =
    isInRadioGroup || selected
      ? themeRaw.spacing.large * 2
      : theme.spacing.small;
  const radioItemRightPadding =
    isInRadioGroup || selected ? 0 : theme.spacing.small;

  return (
    <Inline
      align="left"
      alignY="center"
      as={props.href ? Link : 'div'}
      flex="1"
      gap="small"
      height="100%"
      paddingRight="small"
      width="100%"
      {...props}
      aria-label={selected ? selectedLabel : undefined}
      css={{
        position: 'relative',
        paddingLeft: radioItemLeftPadding,
        paddingRight: radioItemRightPadding,
      }}
    >
      {/* The indicator is conditionally rendered by radix */}
      <RadioItemIndicator>
        <CheckIcon color={disabledOrBaseColor} size={iconSize} />
      </RadioItemIndicator>

      {/* This check icon is for sub menus being selected.  */}
      {selected ? (
        <div css={{ position: 'absolute', left: theme.spacing.small }}>
          <CheckIcon color={disabledOrBaseColor} size={iconSize} />
        </div>
      ) : null}

      {startElement}
      <Text
        css={{
          flex: '1',
          color: styles.textColor,
          paddingRight: theme.spacing.small,
        }}
        size="small"
        weight="medium"
      >
        {children}
      </Text>
      {props.target === '_blank' ? (
        <ExternalLinkIcon color={disabledOrBaseColor} size={iconSize} />
      ) : (
        endElement
      )}
    </Inline>
  );
};

const useStyles = ({
  disabled,
  tone,
}: Pick<ItemBaseProps, 'disabled' | 'tone'>) => {
  const textColor = disabled
    ? theme.semantic.colour.text.textDisabled
    : tone === 'critical'
    ? theme.semantic.colour.text.reserved.critical
    : theme.semantic.colour.text.textPrimary;

  return {
    textColor,
  };
};
